/**
 * BrandReportShareViewer — Public read-only campaign completion report.
 *
 * Mirrors the existing BrandPlanShareViewer pattern (no sidebar, no auth, dark
 * theme, "Powered by Vyvre" footer). Anyone with the signed link can view
 * the KPI tiles + per-creator table and download the PDF / Excel.
 *
 * Strictly additive — does not touch any other share or report code.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  Loader2, BarChart3, ClipboardList, ExternalLink,
  TrendingUp, Eye, Heart, MessageCircle, Share2, Bookmark,
  FileText, FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtNum = (n) => {
  if (n == null) return '—';
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toLocaleString();
};

export const BrandReportShareViewer = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/brand/campaigns/share/${token}/report`);
        setData(data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Link is invalid or has been revoked');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const download = async (kind /* 'pdf' | 'excel' */) => {
    setDownloading(kind);
    try {
      const res = await axios.get(
        `${API_URL}/api/brand/campaigns/share/${token}/report/${kind}`,
        { responseType: 'blob' },
      );
      const mime = kind === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = kind === 'pdf' ? 'pdf' : 'xlsx';
      a.download = `${(data?.campaign_name || 'campaign').replace(/\s+/g, '_')}_report.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0A0B0D] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#FF451A]" size={32} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0A0B0D] flex items-center justify-center p-8">
        <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-12 text-center max-w-md" data-testid="share-report-not-found">
          <ClipboardList className="mx-auto text-[#6B6B6B] dark:text-zinc-500 mb-3" size={44} />
          <h1 className="text-xl font-bold text-[#0F0F0F] dark:text-white mb-1">Report unavailable</h1>
          <p className="text-sm text-[#6B6B6B] dark:text-zinc-400">{error || 'This share link is invalid.'}</p>
        </div>
      </div>
    );
  }

  const s = data.summary || {};
  const totals = s.totals || {};
  const rows = data.creators || [];
  const hasData = (s.reported_creators || 0) > 0;

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0B0D] py-12 px-6" data-testid="brand-report-share-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-2 font-mono">
          Campaign completion report · shared link
        </div>
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-1" data-testid="share-report-title">
              {data.campaign_name || 'Campaign Completion Report'}
            </h1>
            <div className="inline-flex items-center gap-2 text-xs text-[#6B6B6B] dark:text-zinc-400 font-mono">
              <BarChart3 size={12} className="text-[#FF451A]" />
              {s.reported_creators ?? 0} / {s.total_creators ?? 0} creators reported · {s.completion_pct ?? 0}% complete
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => download('pdf')}
              disabled={!hasData || downloading === 'pdf'}
              className="bg-[#FF451A] hover:bg-[#E63E17] text-[#0F0F0F] dark:text-white rounded-full px-4 py-2 text-xs font-medium disabled:opacity-40 inline-flex items-center gap-1.5 transition-colors"
              data-testid="share-report-download-pdf"
            >
              {downloading === 'pdf' ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
              Download PDF
            </button>
            <button
              onClick={() => download('excel')}
              disabled={!hasData || downloading === 'excel'}
              className="bg-[#00C972] hover:bg-[#00C972]/85 text-[#0F0F0F] dark:text-white rounded-full px-4 py-2 text-xs font-medium disabled:opacity-40 inline-flex items-center gap-1.5 transition-colors"
              data-testid="share-report-download-excel"
            >
              {downloading === 'excel' ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />}
              Download Excel
            </button>
          </div>
        </div>

        {/* Aggregate tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-3" data-testid="share-report-aggregates">
          <Tile icon={Eye}           label="Total Views"    value={fmtNum(totals.views)}    color="#06B6D4" />
          <Tile icon={TrendingUp}    label="Total Reach"    value={fmtNum(totals.reach)}    color="#FFB800" />
          <Tile icon={Heart}         label="Total Likes"    value={fmtNum(totals.likes)}    color="#FF3B30" />
          <Tile icon={MessageCircle} label="Total Comments" value={fmtNum(totals.comments)} color="#A855F7" />
          <Tile icon={Share2}        label="Total Shares"   value={fmtNum(totals.shares)}   color="#0055FF" />
          <Tile icon={Bookmark}      label="Total Saves"    value={fmtNum(totals.saves)}    color="#22C55E" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-6">
          <Tile label="Followers Reached" value={fmtNum(s.total_followers_reached)} color="#FF451A" />
          <Tile
            label="Avg Engagement Rate"
            value={s.avg_engagement_rate != null ? `${s.avg_engagement_rate}%` : '—'}
            color="#00C972"
          />
          <Tile
            label="Reporting Coverage"
            value={`${s.reported_creators ?? 0} / ${s.total_creators ?? 0}`}
            sub={`${s.completion_pct ?? 0}%`}
            color="#9B6EFF"
          />
        </div>

        {/* Per-creator table */}
        {!hasData ? (
          <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-8 text-center text-sm text-[#6B6B6B] dark:text-zinc-500" data-testid="share-report-empty">
            No metrics reported yet.
          </div>
        ) : (
          <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl overflow-x-auto" data-testid="share-report-table-wrap">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="bg-white/[0.03] border-b border-black/[0.08] dark:border-white/5">
                <tr className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono">
                  <th className="text-left py-2.5 px-3">Creator</th>
                  <th className="text-right py-2.5 px-2">Views</th>
                  <th className="text-right py-2.5 px-2">Reach</th>
                  <th className="text-right py-2.5 px-2">Likes</th>
                  <th className="text-right py-2.5 px-2">Comments</th>
                  <th className="text-right py-2.5 px-2">Shares</th>
                  <th className="text-right py-2.5 px-2">Saves</th>
                  <th className="text-right py-2.5 px-2">ER %</th>
                  <th className="text-left py-2.5 px-2">Post</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5" data-testid="share-report-rows">
                {rows.map((r) => {
                  const m = r.completion_metrics || {};
                  const reported = !!m.live_post_link;
                  return (
                    <tr key={r.row_id} className={reported ? '' : 'opacity-50'} data-testid={`share-report-row-${r.row_id}`}>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          {r.profile_photo_url ? (
                            <img src={r.profile_photo_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-[#0F0F0F] dark:text-white text-xs font-bold">
                              {r.creator_name?.[0]?.toUpperCase() || '·'}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="text-[#0F0F0F] dark:text-white text-xs font-medium truncate max-w-[180px]">{r.creator_name}</div>
                            {r.instagram_handle && (
                              <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500">@{r.instagram_handle}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 px-2 text-right text-zinc-200 font-mono text-xs">{fmtNum(m.views)}</td>
                      <td className="py-2 px-2 text-right text-zinc-200 font-mono text-xs">{fmtNum(m.reach)}</td>
                      <td className="py-2 px-2 text-right text-zinc-200 font-mono text-xs">{fmtNum(m.likes)}</td>
                      <td className="py-2 px-2 text-right text-zinc-200 font-mono text-xs">{fmtNum(m.comments)}</td>
                      <td className="py-2 px-2 text-right text-zinc-200 font-mono text-xs">{fmtNum(m.shares)}</td>
                      <td className="py-2 px-2 text-right text-zinc-200 font-mono text-xs">{fmtNum(m.saves)}</td>
                      <td className="py-2 px-2 text-right font-mono text-xs">
                        {m.engagement_rate != null ? (
                          <span className="text-[#FF451A]">{m.engagement_rate}%</span>
                        ) : (
                          <span className="text-[#A1A1AA] dark:text-zinc-600">—</span>
                        )}
                      </td>
                      <td className="py-2 px-2">
                        {m.live_post_link ? (
                          <a
                            href={m.live_post_link}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#06B6D4] hover:underline inline-flex items-center gap-1 text-xs font-mono"
                          >
                            <ExternalLink size={10} /> Open
                          </a>
                        ) : (
                          <span className="text-[10px] text-[#A1A1AA] dark:text-zinc-600 font-mono">pending</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="text-center text-[10px] text-[#A1A1AA] dark:text-zinc-600 mt-8 font-mono uppercase tracking-wider">
          Read-only · Powered by Vyvre
        </div>
      </div>
    </div>
  );
};

const Tile = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-3">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1 font-mono">
      {Icon && <Icon size={10} style={{ color }} />} {label}
    </div>
    <div className="text-xl font-bold font-mono" style={{ color }}>{value}</div>
    {sub && <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono mt-0.5">{sub}</div>}
  </div>
);
